import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { trackFb } from "@/lib/analytics";

/**
 * Handles the Emergent-managed Google sign-in redirect.
 * The auth service returns to our app with the one-time session id in the URL fragment
 * (e.g. `/app#session_id=xxxx`). We intercept it BEFORE the router/ProtectedRoute runs,
 * exchange it for our app session, clean the URL, then continue into the app.
 */
export function GoogleAuthGate({ children }) {
  const navigate = useNavigate();
  const { loginWithGoogleSession } = useAuth();
  const hasSession = typeof window !== "undefined" && /[#&]session_id=/.test(window.location.hash || "");
  const [processing, setProcessing] = useState(hasSession);

  useEffect(() => {
    if (!hasSession) return;
    const params = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
    const sid = params.get("session_id");
    const dest = window.location.pathname && window.location.pathname.startsWith("/app")
      ? window.location.pathname
      : "/app";
    (async () => {
      try {
        const data = await loginWithGoogleSession(sid);
        if (data?.is_new) trackFb("CompleteRegistration", { method: "google", status: true }, { eventID: data.user?.id });
        if (data?.linked) {
          // Google matched an account created another way — reassure the user it's now linked.
          toast.success("Google linked to your existing account", {
            description: `We connected Google sign-in to ${data.user?.email}.${data?.has_password ? " You can still use your email & password too." : ""}`,
            duration: 8000,
          });
        } else {
          toast.success(data?.is_new ? "Welcome to Role-play Connect!" : "Signed in with Google");
        }
      } catch (e) {
        toast.error(e?.message || "Google sign-in failed. Please try again.");
      } finally {
        // Strip the session_id fragment so it can't be reused / re-triggered
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        setProcessing(false);
        navigate(dest, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (processing) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background" data-testid="google-auth-processing">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Signing you in…</p>
      </div>
    );
  }
  return children;
}
